import { useCallback, ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';

import { FormField, DatePicker } from '@/shared/ui';
import { DATE_FORMAT } from '@/shared/ui/date-range-picker';
import type { OptionalFieldDefinition } from '@/entities/layout';

import type { OptionalFieldValue } from '../model/types';
import { getFieldLabel, formatDateValue, formatDateForField } from '../lib/utils';

interface OptionalFieldProps {
  fieldKey: string;
  definition: OptionalFieldDefinition;
  value: OptionalFieldValue;
  onChange: (value: OptionalFieldValue) => void;
  error?: string;
  disabled?: boolean;
}

export function OptionalField({ fieldKey, definition, value, onChange, error, disabled = false }: OptionalFieldProps) {
  const { t, i18n } = useTranslation();
  const label = getFieldLabel(definition, i18n.language);

  const handleDateChange = useCallback((date?: Date) => onChange(formatDateForField(date)), [onChange]);

  const handleInputChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const newValue = e.target.value.trim() === '' ? null : e.target.value;
      onChange(newValue);
    },
    [onChange]
  );

  switch (definition.type) {
    case 'DATE': {
      const dateValue = formatDateValue(value);

      return (
        <DatePicker
          id={fieldKey}
          label={label}
          value={dateValue}
          onChange={handleDateChange}
          disabled={disabled}
          dateFormat={DATE_FORMAT}
        />
      );
    }

    case 'NUMERIC':
      return (
        <FormField label={label} name={fieldKey} error={error}>
          <Input
            id={fieldKey}
            type="number"
            min="0"
            step="1"
            value={value || ''}
            onChange={handleInputChange}
            disabled={disabled}
            placeholder={t('validity.valueMissing.select')}
          />
        </FormField>
      );

    case 'TEXT':
    default:
      return (
        <FormField label={label} name={fieldKey} error={error}>
          <Input
            id={fieldKey}
            type="text"
            value={value || ''}
            onChange={handleInputChange}
            disabled={disabled}
            placeholder={t('validity.valueMissing.select')}
          />
        </FormField>
      );
  }
}
